import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef,
    FC,
    ReactNode,
} from 'react';
import { type NavigateOptions, useNavigate } from 'react-router';
import { NavigationGuardModal } from './components';

interface NavigationGuardContextValue {
    setIsDirty: (dirty: boolean) => void;
    guardedNavigate: (to: string | number, options?: NavigateOptions) => void;
}

const NavigationGuardContext =
    createContext<NavigationGuardContextValue | null>(null);

interface NavigationGuardProviderProps {
    children: ReactNode;
}

type NavigationInfo =
    | {
          to: string;
          options?: NavigateOptions;
      }
    | {
          to: string | number;
      };

export const NavigationGuardProvider: FC<NavigationGuardProviderProps> = ({
    children,
}) => {
    const navigate = useNavigate();
    const isDirtyRef = useRef(false);
    const [pendingTarget, setPendingTarget] = useState<NavigationInfo | null>(
        null,
    );

    const performNavigate = useCallback(
        (to: string | number, options?: NavigateOptions) => {
            if (typeof to === 'number') {
                navigate(to);
            } else {
                navigate(to, options);
            }
        },
        [navigate],
    );

    const setIsDirty = useCallback((dirty: boolean) => {
        isDirtyRef.current = dirty;
    }, []);

    const guardedNavigate = useCallback(
        (to: string | number, options?: NavigateOptions) => {
            if (isDirtyRef.current) {
                setPendingTarget({ to, options });
            } else {
                performNavigate(to, options);
            }
        },
        [performNavigate],
    );

    const handleConfirm = useCallback(() => {
        const target = pendingTarget;
        setPendingTarget(null);
        isDirtyRef.current = false;
        if (target === null) {
            return;
        }

        if ('options' in target) {
            performNavigate(target.to, target.options);
        } else {
            performNavigate(target.to);
        }
    }, [pendingTarget, performNavigate]);

    const handleCancel = useCallback(() => {
        setPendingTarget(null);
    }, []);

    return (
        <NavigationGuardContext.Provider
            value={{ setIsDirty, guardedNavigate }}
        >
            {children}

            <NavigationGuardModal
                open={pendingTarget !== null}
                onConfirm={handleConfirm}
                onClose={handleCancel}
            />
        </NavigationGuardContext.Provider>
    );
};

export const useNavigationGuard = (): NavigationGuardContextValue => {
    const context = useContext(NavigationGuardContext);
    if (!context) {
        throw new Error(
            'useNavigationGuard must be used within a NavigationGuardProvider',
        );
    }
    return context;
};

/**
 * Hook for page components to register dirty state with the navigation guard.
 * Automatically clears dirty state on unmount.
 */
export const useNavigationDirty = (): ((isDirty: boolean) => void) => {
    const { setIsDirty } = useNavigationGuard();

    useEffect((): (() => void) => (): void => setIsDirty(false), [setIsDirty]);

    return setIsDirty;
};

/**
 * Component that syncs a dirty flag to the navigation guard via useEffect.
 * Drop-in replacement for RouterPrompt in render functions.
 */
export const NavigationDirtySync: FC<{ dirty: boolean }> = ({ dirty }) => {
    const { setIsDirty } = useNavigationGuard();

    useEffect(() => {
        setIsDirty(dirty);
        return (): void => setIsDirty(false);
    }, [dirty, setIsDirty]);

    return null;
};
